import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, PenLine, Loader, Copy, Download, Check, Sparkles,
  Undo2, ListChecks, RefreshCw, AlertCircle, Settings2,
} from 'lucide-react';
import {
  fetchReplyOutline, generateWorkbenchDraft, refineReplySpan,
  ReplyConcept, DraftError, DraftStance,
} from '../lib/draftService';
import { fetchProceedingDigest } from '../lib/digestService';
import { useAuth } from '../contexts/AuthContext';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { track } from '../utils/analytics';

const STANCES: DraftStance[] = ['support', 'oppose', 'modify'];

const REFINE_PRESETS: { label: string; instr: string }[] = [
  { label: 'Strengthen', instr: 'Make this argument more persuasive and forceful, while staying measured and lawyerly.' },
  { label: 'Soften', instr: 'Soften and hedge this so it does not overclaim; keep it defensible and measured.' },
  { label: 'More concise', instr: 'Tighten this passage — cut redundancy without losing substance.' },
  { label: 'More formal', instr: 'Raise the register to a formal FCC filing tone.' },
  { label: 'Rebut harder', instr: 'Sharpen the rebuttal of the opposing arguments named in this passage, grounded only in the record.' },
  { label: 'Add counterargument', instr: 'Anticipate the strongest opposing counterargument to this passage and rebut it, grounded only in the record.' },
  { label: 'Tie to our interest', instr: 'Connect this argument back to the filer’s described interest and who they are.' },
  { label: 'Mark where a cite is needed', instr: 'Insert a clearly-bracketed placeholder where a supporting record cite or authority should go — do not invent one.' },
];

/** Max selection the backend refines in one call; larger selections are refused in the UI
 *  (must match reply_workbench._MAX_SELECTION) so we never overwrite an untruncated span with
 *  a replacement generated from truncated input. */
const MAX_REFINE_SELECTION = 6000;

interface Persisted {
  stance: DraftStance;
  whoYouAre: string;
  argumentsText: string;
  selected: string[];
  text: string;
}

const storeKey = (d: string) => `cg.reply_workbench.${d}`;

type Banner =
  | { type: 'auth' }
  | { type: 'tier'; msg: string }
  | { type: 'nodigest' }
  | { type: 'error'; msg: string }
  | null;

/** Full-page, two-pane reply-comment drafting workspace (Prosumer/Pro).
 *  Left: the draft (markdown, editable, autosaved, downloadable). Right: a tool
 *  rail — ① your position, ② concepts raised (pick what to engage), ③ generate,
 *  then localized inline refine on any selection. A drafting aid grounded in the
 *  record — it never supplies facts, only bracketed placeholders. */
export const ReplyWorkbenchPage: React.FC = () => {
  const { docketNumber = '' } = useParams<{ docketNumber: string }>();
  // Remount per docket (keyed) so each docket's autosaved session loads fresh via
  // the lazy state initializers below — no cross-docket state bleed or clobber.
  return <ReplyWorkbenchInner key={docketNumber} docket={docketNumber} />;
};

const ReplyWorkbenchInner: React.FC<{ docket: string }> = ({ docket }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const requireAuth = useRequireAuth();

  // This docket's autosaved session, read once at mount (component is keyed per docket).
  const [saved] = useState<Partial<Persisted>>(() => {
    try { const raw = localStorage.getItem(storeKey(docket)); return raw ? JSON.parse(raw) : {}; }
    catch { return {}; }
  });

  // Whether the proceeding is analyzed at all (public digest check).
  const [analyzed, setAnalyzed] = useState<boolean | null>(null);

  // ── Setup (right rail) ── initialized from the saved session.
  const [stance, setStance] = useState<DraftStance>(saved.stance || 'oppose');
  const [whoYouAre, setWhoYouAre] = useState(saved.whoYouAre || '');
  const [argumentsText, setArgumentsText] = useState(saved.argumentsText || '');

  // ── Concepts (outline) ──
  const [concepts, setConcepts] = useState<ReplyConcept[]>([]);
  const [selected, setSelected] = useState<string[]>(saved.selected || []);
  const [outlineState, setOutlineState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  // ── Draft (left pane) ──
  const [text, setText] = useState(saved.text || '');
  const [generating, setGenerating] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);

  // ── Refine ──
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [sel, setSel] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [refining, setRefining] = useState(false);
  const [customInstr, setCustomInstr] = useState('');
  const [refineNote, setRefineNote] = useState<string | null>(null);
  // A proposed refine awaiting Accept/Reject — we do NOT overwrite the draft until accepted.
  const [pending, setPending] = useState<{ start: number; end: number; original: string; replacement: string } | null>(null);

  const UNDO_MAX = 25;
  const pushUndo = (snapshot: string) => setUndoStack((s) => [...s.slice(-(UNDO_MAX - 1)), snapshot]);
  const undo = () => {
    if (!undoStack.length) return;
    setText(undoStack[undoStack.length - 1]);
    setUndoStack((s) => s.slice(0, -1));
    setPending(null);
    setSel({ start: 0, end: 0 });
  };
  const acceptRefine = () => {
    if (!pending) return;
    pushUndo(text);
    setText(text.slice(0, pending.start) + pending.replacement + text.slice(pending.end));
    setPending(null);
    setSel({ start: 0, end: 0 });
  };
  const rejectRefine = () => setPending(null);

  const [banner, setBanner] = useState<Banner>(null);
  const [copied, setCopied] = useState(false);

  // Public analyzed-check.
  useEffect(() => {
    let alive = true;
    fetchProceedingDigest(docket)
      .then((d) => { if (alive) setAnalyzed(!!d && d.status === 'ok'); })
      .catch(() => { if (alive) setAnalyzed(false); });
    return () => { alive = false; };
  }, [docket]);

  // Autosave the session (survives the magic-link / Stripe reloads). The initial
  // write mirrors the values we just loaded, so it can't clobber saved data.
  useEffect(() => {
    const p: Persisted = { stance, whoYouAre, argumentsText, selected, text };
    try { localStorage.setItem(storeKey(docket), JSON.stringify(p)); } catch { /* ignore */ }
  }, [docket, stance, whoYouAre, argumentsText, selected, text]);

  const argumentsList = useMemo(
    () => argumentsText.split('\n').map((s) => s.trim()).filter(Boolean),
    [argumentsText],
  );

  // "Facts to fill in" chips: only real user-supplied facts — never structural or
  // system-known placeholders (the backend strips these too, this is a UI backstop).
  const STRUCTURAL_PLACEHOLDER = /^\[\s*(introduction|conclusion|argument|body|discussion|proceeding title|name)\s*\]$/i;
  const placeholders = useMemo(
    () => Array.from(new Set(text.match(/\[[^\]\n]{3,90}\]/g) || []))
      .filter((p) => !STRUCTURAL_PLACEHOLDER.test(p)),
    [text],
  );

  // Map a coded error onto a banner / auth flow. Returns true if handled.
  const handleGate = useCallback((e: unknown): boolean => {
    const err = e as DraftError;
    if (err?.code === 'AUTH') {
      setBanner({ type: 'auth' });
      requireAuth(() => { /* resumed by the user re-clicking after sign-in */ }, {
        intentLabel: 'draft a reply comment', intentKey: `reply_wb:${docket}`,
      });
      return true;
    }
    if (err?.code === 'TIER' || err?.code === 'LIMIT') {
      setBanner({ type: 'tier', msg: err.message }); return true;
    }
    if (err?.code === 'NO_DIGEST') { setBanner({ type: 'nodigest' }); return true; }
    setBanner({ type: 'error', msg: err?.message || 'Something went wrong.' });
    return true;
  }, [docket, requireAuth]);

  const loadConcepts = useCallback(async () => {
    setOutlineState('loading');
    setBanner(null);
    try {
      const o = await fetchReplyOutline(docket, argumentsList);
      const cs = o.concepts || [];
      setConcepts(cs);
      // Reset the selection to this outline's pre-checked matches. The concept ids (c1..cN) are
      // reused across regenerations but point at DIFFERENT concepts each run, so merging old
      // checks into a fresh outline would silently re-target them — reconcile to the new set.
      const suggested = (o.suggested_ids || []).filter((id) => cs.some((c) => c.id === id));
      setSelected(suggested);
      setOutlineState('ready');
      track('reply_outline_loaded', { docket_number: docket, n: cs.length, matched: suggested.length });
    } catch (e) {
      setOutlineState('error');
      handleGate(e);
    }
  }, [docket, argumentsList, handleGate]);

  const toggleConcept = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const generate = useCallback(async () => {
    setGenerating(true);
    setBanner(null);
    track('reply_wb_generate', { docket_number: docket, stance, n_concepts: selected.length });
    try {
      const d = await generateWorkbenchDraft(docket, {
        stance,
        concepts: concepts.length ? concepts : undefined,
        concept_ids: selected.length ? selected : undefined,
        who_you_are: whoYouAre.trim() || undefined,
        arguments: argumentsList.length ? argumentsList : undefined,
      });
      if (text.trim()) pushUndo(text);
      setText(d.draft_markdown);
      setPending(null);
      setSel({ start: 0, end: 0 }); // captured range is stale once the text changes
      track('reply_wb_success', { docket_number: docket, stance });
    } catch (e) {
      handleGate(e);
    } finally {
      setGenerating(false);
    }
  }, [docket, stance, concepts, selected, whoYouAre, argumentsList, text, handleGate]);

  const captureSel = () => {
    const ta = taRef.current;
    if (ta) setSel({ start: ta.selectionStart, end: ta.selectionEnd });
  };

  const runRefine = useCallback(async (instruction: string) => {
    if (pending) { setRefineNote('Accept or reject the pending change first.'); return; }
    // Read offsets fresh from the DOM (selection survives blur); the stored `sel`
    // can be stale after paste/cut/drag edits that don't fire our capture handlers.
    const ta = taRef.current;
    const start = ta ? ta.selectionStart : sel.start;
    const end = ta ? ta.selectionEnd : sel.end;
    if (start === end) { setRefineNote('Select some text in the draft first, then choose a refine action.'); return; }
    if (end - start > MAX_REFINE_SELECTION) {
      setRefineNote(`That selection is too long to refine in one step (max ~${MAX_REFINE_SELECTION.toLocaleString()} characters). Select a smaller passage.`);
      return;
    }
    setRefining(true);
    setRefineNote(null);
    setBanner(null);
    const selection = text.slice(start, end);
    const before = text.slice(0, start);
    const after = text.slice(end);
    try {
      const { replacement } = await refineReplySpan(docket, {
        selection, instruction, before, after, who_you_are: whoYouAre.trim() || undefined,
      });
      // Propose the change as a redline — do NOT overwrite the attorney's text until they accept.
      setPending({ start, end, original: selection, replacement });
      setCustomInstr('');
      track('reply_refine', { docket_number: docket });
    } catch (e) {
      handleGate(e);
    } finally {
      setRefining(false);
    }
  }, [pending, sel, text, docket, whoYouAre, handleGate]);

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* ignore */ }
  };
  const downloadMd = () => {
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `reply-comment-${docket}.md`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadDoc = () => {
    // Word-openable HTML doc — convert the markdown so headings/bold render as
    // formatting instead of leaking literal '###'/'**' into the document.
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const inline = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    const body = text.split('\n').map((l) => {
      const h = l.match(/^(#{1,6})\s+(.*)$/);
      if (h) return `<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`;
      if (l.trim() === '') return '<p>&nbsp;</p>';
      return `<p>${inline(l)}</p>`;
    }).join('');
    const html = `<html xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"></head><body>${body}</body></html>`;
    const blob = new Blob([html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `reply-comment-${docket}.doc`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Not-analyzed guard ──
  if (analyzed === false) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Link to={`/proceedings/${docket}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to {docket}
        </Link>
        <div className="rounded-xl border border-cg-border bg-white p-6">
          <h1 className="text-lg font-semibold flex items-center gap-2"><PenLine className="w-5 h-5 text-cg-green" /> Reply comment workspace</h1>
          <p className="mt-2 text-slate-600">
            This proceeding isn’t analyzed yet — reply drafting is grounded in its comment record.
            Open the docket and generate its digest first.
          </p>
          <Link to={`/proceedings/${docket}`} className="mt-4 inline-flex rounded-lg bg-cg-green px-4 py-2 text-white text-sm font-medium">
            Go to {docket} →
          </Link>
        </div>
      </div>
    );
  }

  const canGenerate = !generating && (analyzed !== null) && !authLoading;
  const hasDraft = text.trim().length > 0;

  // ── Right-rail blocks (arranged below: setup pre-draft, refine-first post-draft) ──
  const positionSection = (
    <section className="rounded-xl border border-cg-border bg-white p-4">
      <h2 className="text-sm font-semibold mb-1">① Your position</h2>
      <p className="text-[12px] text-slate-500 mb-2">
        A few bullets of what you want to argue — we’ll substantiate them against what others filed.
      </p>
      <textarea value={argumentsText} onChange={(e) => setArgumentsText(e.target.value)} rows={6}
        placeholder={'One point per line, e.g.\nThe resale restriction would harm competition\nExisting STIR/SHAKEN rules already address this\nSmall providers need a phased timeline'}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500">Stance</span>
        {STANCES.map((s) => (
          <button key={s} onClick={() => setStance(s)}
            className={`px-2.5 py-1 rounded-lg text-[13px] border capitalize ${stance === s ? 'bg-cg-green text-white border-cg-green' : 'bg-white border-slate-200 text-slate-600'}`}>
            {s}
          </button>
        ))}
      </div>
      <label className="text-xs uppercase tracking-wide text-slate-500 mt-3 block">Who you are &amp; your interest <span className="normal-case tracking-normal text-slate-400">(optional)</span></label>
      <textarea value={whoYouAre} onChange={(e) => setWhoYouAre(e.target.value)} rows={3}
        placeholder="e.g. A rural fixed-wireless provider; the proposal raises our compliance costs"
        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
    </section>
  );

  const conceptsSection = (
    <section className="rounded-xl border border-cg-border bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-cg-green" /> ② Concepts raised</h2>
        {outlineState === 'ready' && (
          <button onClick={loadConcepts} className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> reload</button>
        )}
      </div>
      {outlineState === 'idle' && (
        <>
          <p className="text-[13px] text-slate-500 mb-2">See what’s already been argued in this docket. We’ll pre-check the closest matches to your position — add any we missed, or uncheck what you don’t want to engage.</p>
          <button onClick={loadConcepts}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-cg-green text-cg-green px-3 py-1.5 text-sm font-medium">
            <ListChecks className="w-4 h-4" /> See what’s been argued →
          </button>
        </>
      )}
      {outlineState === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><Loader className="w-4 h-4 animate-spin" /> Reading the record…</div>
      )}
      {outlineState === 'error' && (
        <button onClick={loadConcepts} className="text-sm text-cg-green underline">Try loading the concepts again</button>
      )}
      {outlineState === 'ready' && (
        <div className="space-y-1.5 max-h-[55vh] overflow-auto pr-1">
          {concepts.map((c) => (
            <label key={c.id} className={`block rounded-lg border p-2 cursor-pointer ${selected.includes(c.id) ? 'border-cg-green bg-cg-green/5' : 'border-slate-200'}`}>
              <div className="flex items-start gap-2">
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleConcept(c.id)} className="mt-1" />
                <span>
                  <b className="block text-[13px]">{c.title}</b>
                  {c.summary && <span className="block text-[12px] text-slate-500">{c.summary}</span>}
                  <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5 text-[11px]">
                    {c.lead_voices && c.lead_voices.length > 0 ? (
                      <span className="text-cg-green font-medium">Led by {c.lead_voices.join(', ')}</span>
                    ) : c.voice_type === 'public' ? (
                      <span className="text-slate-500">Broad public sentiment</span>
                    ) : c.raised_by && c.raised_by.length > 0 ? (
                      <span className="text-slate-400">raised by {c.raised_by.slice(0, 3).join(', ')}</span>
                    ) : null}
                    {typeof c.n_supporters === 'number' && c.n_supporters > 1 && (
                      <span className="rounded-full bg-slate-100 text-slate-500 px-1.5">{c.n_supporters} filers on this issue</span>
                    )}
                  </span>
                </span>
              </div>
            </label>
          ))}
          <p className="text-[11px] text-slate-400 pt-1">Pre-checked = closest matches to your position (we may miss some) — adjust freely. None checked = engage all.</p>
        </div>
      )}
    </section>
  );

  const generateSection = (
    <section className="rounded-xl border border-cg-border bg-white p-4">
      <h2 className="text-sm font-semibold mb-2">③ Substantiate</h2>
      <button onClick={generate} disabled={!canGenerate}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-cg-green px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50">
        {generating ? <><Loader className="w-4 h-4 animate-spin" /> Substantiating…</> : <><Sparkles className="w-4 h-4" /> {hasDraft ? 'Re-substantiate' : 'Substantiate my position'}</>}
      </button>
      {generating ? (
        <p className="text-[11px] text-slate-500 mt-1.5">Grounding your position in the record and drafting — this usually takes ~30 seconds.</p>
      ) : hasDraft ? (
        <p className="text-[11px] text-slate-400 mt-1.5">This replaces the current draft (Undo is available).</p>
      ) : (
        <p className="text-[11px] text-slate-400 mt-1.5">Takes ~30 seconds — we read the record and draft cited arguments.</p>
      )}
    </section>
  );

  const refineSection = (
    <section className="rounded-xl border border-cg-border bg-white p-4">
      <h2 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-cg-green" /> Refine a selection</h2>
      <p className="text-[12px] text-slate-500 mb-2">
        Highlight any passage — generated or your own — then pick an action. You’ll see the change and can accept or reject it.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {REFINE_PRESETS.map((r) => (
          <button key={r.label} onClick={() => runRefine(r.instr)} disabled={refining || !!pending}
            className="text-[12px] rounded-full border border-slate-200 px-2.5 py-1 hover:border-cg-green disabled:opacity-50">
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={customInstr} onChange={(e) => setCustomInstr(e.target.value)}
          placeholder="Or type an instruction…" disabled={!!pending}
          onKeyDown={(e) => { if (e.key === 'Enter' && customInstr.trim()) runRefine(customInstr.trim()); }}
          className="flex-1 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[13px] disabled:bg-slate-50" />
        <button onClick={() => customInstr.trim() && runRefine(customInstr.trim())} disabled={refining || !!pending || !customInstr.trim()}
          className="rounded-lg bg-cg-green px-3 text-sm text-white disabled:opacity-50">Go</button>
      </div>
      {pending && <p className="text-[12px] text-cg-green mt-2">Review the proposed change on the left — Accept or Reject it.</p>}
      {refineNote && <p className="text-[12px] text-amber-700 mt-2">{refineNote}</p>}
    </section>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Link to={`/proceedings/${docket}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back to {docket}
          </Link>
          <h1 className="mt-1 text-xl font-semibold flex items-center gap-2 flex-wrap">
            <PenLine className="w-5 h-5 text-cg-green" /> Substantiate your position
            <span className="rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-amber-200">Beta</span>
            <span className="text-slate-400 font-normal text-base">· {docket}</span>
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            State your position; we ground it in what others filed in this docket.
            <span className="text-slate-400"> A drafting aid in beta — always review the output before filing.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {undoStack.length > 0 && (
            <button onClick={undo}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
              <Undo2 className="w-4 h-4" /> Undo
            </button>
          )}
          <button onClick={copyDraft} disabled={!text}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">
            {copied ? <Check className="w-4 h-4 text-cg-green" /> : <Copy className="w-4 h-4" />} Copy
          </button>
          <button onClick={downloadMd} disabled={!text}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">
            <Download className="w-4 h-4" /> .md
          </button>
          <button onClick={downloadDoc} disabled={!text}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">
            <Download className="w-4 h-4" /> .doc
          </button>
        </div>
      </div>

      {banner && (
        <div className="mb-4 rounded-lg border px-4 py-3 text-sm flex items-start gap-2"
          style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-amber-800">
            {banner.type === 'auth' && <>Sign in to draft a reply — your setup on this page is saved.</>}
            {banner.type === 'tier' && (
              <>{banner.msg} Your setup is saved.{' '}
                <button onClick={() => navigate('/pricing')} className="underline font-medium">Upgrade &amp; continue →</button></>
            )}
            {banner.type === 'nodigest' && <>This proceeding isn’t analyzed yet — open its digest first.</>}
            {banner.type === 'error' && <>{banner.msg}</>}
          </div>
        </div>
      )}

      {/* Two-pane working window */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-stretch">
        {/* LEFT — the draft */}
        <div className="rounded-xl border border-cg-border bg-white p-4 min-h-[70vh] flex flex-col order-2 lg:order-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">Your reply comment — write or edit freely</span>
            {refining && <span className="inline-flex items-center gap-1 text-xs text-cg-green"><Loader className="w-3.5 h-3.5 animate-spin" /> refining selection…</span>}
          </div>

          {/* Proposed refine (redline) — accept or reject before it touches the draft. */}
          {pending && (
            <div className="mb-3 rounded-lg border border-cg-green/40 bg-cg-green/5 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">Proposed change — review before applying</div>
              <div className="space-y-1.5 text-[13px] leading-relaxed max-h-[40vh] overflow-auto">
                <div>
                  <span className="text-[10px] uppercase tracking-wide text-red-500">Replacing</span>
                  <div className="rounded bg-red-50 border border-red-100 px-2 py-1.5 text-red-700 whitespace-pre-wrap">{pending.original}</div>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wide text-emerald-600">With</span>
                  <div className="rounded bg-emerald-50 border border-emerald-100 px-2 py-1.5 text-emerald-800 whitespace-pre-wrap">{pending.replacement}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={acceptRefine} className="inline-flex items-center gap-1 rounded-lg bg-cg-green px-3 py-1.5 text-sm font-medium text-white"><Check className="w-4 h-4" /> Accept</button>
                <button onClick={rejectRefine} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600">Reject</button>
              </div>
            </div>
          )}

          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onSelect={captureSel} onMouseUp={captureSel} onKeyUp={captureSel}
            readOnly={!!pending}
            placeholder={'Generate a first draft from the panel on the right — or paste / type your own reply comment here, then select any passage and refine it.'}
            className={`flex-1 w-full resize-none border rounded-lg px-4 py-3 text-[13.5px] font-mono leading-relaxed ${pending ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-200'}`}
            spellCheck
          />
          {placeholders.length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Facts to fill in ({placeholders.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map((p, i) => (
                  <span key={i} className="text-[12px] rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5">{p}</span>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">These are yours to supply — CommsGenie won’t invent facts or figures.</p>
            </div>
          )}
        </div>

        {/* RIGHT — tool rail. Pre-draft: setup (position → concepts → generate).
            Post-draft: refine-first, with setup tucked into a collapsible. */}
        <div className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-4 self-start">
          {hasDraft ? (
            <>
              {refineSection}
              <details className="group">
                <summary className="cursor-pointer list-none rounded-xl border border-cg-border bg-white px-4 py-3 text-sm font-semibold flex items-center gap-2 select-none">
                  <Settings2 className="w-4 h-4 text-slate-500" /> Adjust position, concepts &amp; regenerate
                  <span className="ml-auto text-xs font-normal text-slate-400 group-open:hidden">show</span>
                  <span className="ml-auto text-xs font-normal text-slate-400 hidden group-open:inline">hide</span>
                </summary>
                <div className="space-y-4 mt-4">
                  {positionSection}
                  {conceptsSection}
                  {generateSection}
                </div>
              </details>
            </>
          ) : (
            <>
              {positionSection}
              {conceptsSection}
              {generateSection}
            </>
          )}

          <p className="text-[11px] text-slate-400 px-1">
            A drafting aid grounded in the public FCC comment record — a starting point, not legal advice.
            Verify every citation before filing. Facts in [brackets] are yours to supply.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReplyWorkbenchPage;
